/** @jsxImportSource @emotion/react */

import React from 'react';
import { useTranslation } from '../../../contexts/TranslationContext/TranslationContext';
import { CSSProp } from '../../../types/emotion';

export type LanguageSelectProps = {
  /**
   * Supported languages
   */
  supportedLanguages: string[];
  /**
   * Change locale handler
   */
  onLocaleChange: (locale: string) => void;
};

export function LanguageSelect({
  supportedLanguages,
  onLocaleChange,
  ...props
}: LanguageSelectProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof LanguageSelectProps> & CSSProp) {
  const { lang } = useTranslation();

  return (
    <div
      css={{
        position: 'relative',
        '&::after': {
          backgroundImage:
            'url("data:image/svg+xml;base64,PHN2ZyBjbGFzcz0iY3NzLTF3ZW9oNXgiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTIxLjAwMDIgOC4wMDAyNUMyMS4wMDAyIDguMjU2MjUgMjAuOTAyMyA4LjUxMjI1IDIwLjcwNzMgOC43MDcyNUwxMi43MDcyIDE2LjcwNzJDMTIuMzE2MiAxNy4wOTgyIDExLjY4NDIgMTcuMDk4MiAxMS4yOTMzIDE2LjcwNzJMMy4yOTMyNSA4LjcwNzI1QzIuOTAyMjUgOC4zMTYyNSAyLjkwMjI1IDcuNjg0MjUgMy4yOTMyNSA3LjI5MzI1QzMuNjg0MjUgNi45MDIyNSA0LjMxNjI1IDYuOTAyMjUgNC43MDcyNSA3LjI5MzI1TDEyLjAwMDIgMTQuNTg2MkwxOS4yOTMyIDcuMjkzMjVDMTkuNjg0MiA2LjkwMjI1IDIwLjMxNjIgNi45MDIyNSAyMC43MDczIDcuMjkzMjVDMjAuOTAyMyA3LjQ4ODI1IDIxLjAwMDIgNy43NDQyNSAyMS4wMDAyIDguMDAwMjVaIiBmaWxsPSJjdXJyZW50Q29sb3IiPjwvcGF0aD48L3N2Zz4=")',
          backgroundPosition: 'right 10px center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'auto 80%',
          content: '""',
          height: '1.5rem',
          pointerEvents: 'none',
          position: 'absolute',
          right: '.7rem',
          top: '.75rem',
          width: '2rem',
        },
      }}
      {...props}
    >
      <select
        data-testid="dssf-language-select"
        css={{
          backgroundColor: 'transparent !important',
          minWidth: '3rem',
          border: 'none',
          fontSize: '1rem',
          appearance: 'none',
          height: '3rem',
          overflow: 'hidden',
          padding: '0 2.75rem 0 1rem',
          textOverflow: 'ellipsis',
          width: '100%',
          cursor: 'pointer',
        }}
        value={lang}
        onChange={item => onLocaleChange(item.target.value)}
      >
        {supportedLanguages.map(item => (
          <option key={item} value={item} data-testid={`dssf-language-option-${item}`}>
            {item.toUpperCase()}
          </option>
        ))}
      </select>
    </div>
  );
}
